import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import arraySupport from "dayjs/plugin/arraySupport";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import duration from "dayjs/plugin/duration";

import { useUserStore } from "../stores/index.js";

import DateFormatMapping from "../../constants/format.json";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(arraySupport);
dayjs.extend(quarterOfYear);
dayjs.extend(duration);

function getDateFormat(locale) {
  if (!locale) {
    return DateFormatMapping["en"];
  } else if (DateFormatMapping[locale]) {
    return DateFormatMapping[locale];
  } else if (DateFormatMapping[locale.split("-")[0]]) {
    let localeID = locale.split("-")[1];
    locale = localeID && locale.split("-")[0] + "-" + localeID.toUpperCase();
    if (DateFormatMapping[locale]) {
      return DateFormatMapping[locale];
    } else {
      return DateFormatMapping[locale.split("-")[0]];
    }
  } else {
    return DateFormatMapping["en"];
  }
}

export function formatDate(date, locale) {
  if (!date) return null;
  const currentLocale = locale || window.accountDetails.DateFormat;

  let dateObj = dayjs(date);

  if (!dateObj.isValid()) {
    dateObj = dayjs(date.split(" ")[0]);
  }

  if (!dateObj.isValid()) {
    return null;
  }

  switch (currentLocale) {
    case "ISO":
      return dateObj.format(getDateFormat(currentLocale).DateFormat);
    case "US":
      return new Intl.DateTimeFormat("en-US").format(dateObj.toDate());
    case "EU":
      return new Intl.DateTimeFormat("en-150").format(dateObj.toDate());
    default:
      return dateObj.toDate().toLocaleDateString();
  }
}

export function formatDateTime(date, locale, forceSplit) {
  if (!date || typeof date !== "string") return null;
  const currentLocale =
    locale || (window.accountDetails && window.accountDetails.DateFormat);

  let dateObj = dayjs(date);

  if (!dateObj.isValid() || forceSplit) {
    let value = date && date.split(/[^0-9]/);
    dateObj = dayjs([value[0], value[1] - 1, value[2], value[3], value[4]]);
  }

  if (!dateObj.isValid()) {
    return null;
  }

  let localeId = getLocaleId(currentLocale);

  let options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: getDateFormat(localeId).DateTimeFormat.split(" ")[2]
  };

  switch (currentLocale) {
    case "ISO":
      return dateObj.toISOString();
    case "US":
      return new Intl.DateTimeFormat("en-US", options).format(dateObj.toDate());
    case "EU":
      return new Intl.DateTimeFormat("en-150", options).format(
        dateObj.toDate()
      );
    default:
      return new Intl.DateTimeFormat(navigator.language, options).format(
        dateObj.toDate()
      );
  }
}

export function dateFormatString(locale) {
  locale = getLocaleId(locale);
  let format = getDateFormat(locale).DateFormat;
  format = format.replace(/d/g, "D").replace(/y/g, "Y");
  return format;
}

export function dateTimeFormatString(locale) {
  locale = getLocaleId(locale);
  let format = getDateFormat(locale).DateTimeFormat;
  format = format.replace(/d/g, "D").replace(/y/g, "Y");
  return format;
}

/**
 * It accept the date in UTC format and based on user timezone return date value
 * @param {string} dateString
 * @param {string} locale - date format choosed based on locale : US,EU, and also ISO
 * @param {object} timeZone 

 * @returns {string}
 */
export function formatToTimeZone(dateString, locale, timeZone) {
  if (!dateString) return;
  locale = locale || window.accountDetails.DateFormat;
  let dateTimeFormat = dateTimeFormatString(locale);
  let userTimeZone = timeZone
    ? timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
  let dateTimeString = dateString.split(" ")[0];
  let dateTimeObj = dayjs(dateTimeString);
  let localeId = getLocaleId(locale);

  let parsedDateTimeWithZone = new Intl.DateTimeFormat("en-Us", {
    dateStyle: "short",
    timeStyle: "short",
    hour12: getDateFormat(localeId).DateTimeFormat.split(" ")[2]
  }).format(dateTimeObj.toDate());

  if (locale === "ISO")
    return (
      dayjs(parsedDateTimeWithZone).utc().toISOString().split(".")[0] + "Z"
    );

  return dayjs(parsedDateTimeWithZone).tz(userTimeZone).format(dateTimeFormat);
}

export function getTimeZoneAbbr(timeZone) {
  let unAbbrevated = timeZone ? dayjs().tz(timeZone).format("zzz") : null;
  if (unAbbrevated) {
    var matches = unAbbrevated.match(/\b(\w)/g);
    return matches.join("");
  }
  return null;
}

export function getTimeZoneOffset(timeZone, date = new Date()) {
  return timeZone ? dayjs.tz(date, timeZone).format("Z") : null;
}

export function getRelativeTime(date, withoutSuffix) {
  return dayjs(date).fromNow(withoutSuffix);
}

export function getFormattedDate(date, format, currentFormat) {
  return currentFormat
    ? dayjs(date, currentFormat).format(format)
    : dayjs(date).format(format);
}

export function getFormattedUTCDate(date, format) {
  return dayjs(date).utc().format(format);
}

export function checkIsSameOrAfter(date) {
  return dayjs().isSameOrAfter(date);
}

export function getDayjsObject(date) {
  return date ? dayjs(date) : dayjs();
}

export function getDayjs() {
  return dayjs;
}

export function formatDateToUserDate(givendate, withTime) {
  const { currentUser } = useUserStore.getState();
  var timezone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  let date = new Date(
    givendate.toLocaleDateString("en-US", { timeZone: timezone })
  );
  let month = date.getMonth();
  let day = date.getDate();
  let year = date.getFullYear();
  let hours = date.getHours();
  let minutes = date.getMinutes();
  if (!withTime) {
    let dateString = `${dayjs([year, month, day]).format("YYYY-MM-DD")}`;
    return dateString;
  }
  let datetimeString = `${dayjs([year, month, day, hours, minutes])
    .tz(timezone)
    .format("YYYY-MM-DDTHH:mm:00Z")} ${timezone}`;
  return datetimeString;
}

function getLocaleId(locale) {
  if (locale === "EU") {
    locale = "en-150";
  } else if (locale === "US") {
    locale = "en-US";
  }
  if (!locale || locale === "LOCAL") {
    locale = navigator.language;
  }
  return locale;
}

export function setMonth(date, defaultDate, month, format) {
  return dayjs(date ? date : defaultDate)
    .set("month", month)
    .format(format);
}
export function setYear(date, defaultDate, year, format) {
  return dayjs(date ? date : defaultDate)
    .set("year", year)
    .format(format);
}

/**
 * It accept the date in UTC format and based on user timezone return date value
 * @param {string} dateString
 * @param {boolean} isformat
 * @returns {string}
 */
export function formatToUserTimeZone(dateString, isformat) {
  if (!dateString) return;
  // temp fix :: date and time is object because of migration.
  if (dateString && typeof dateString == "object") {
    dateString = dateString?.dv;
  }
  const { currentUser } = useUserStore.getState();
  let userTimeZone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (isISODateWithZ(dateString) && currentUser?.DateFormat === "ISO") {
    return dateString;
  }

  let dateTimeString = dateString.split(" ")[0];
  let dateTimeObj = dayjs(dateTimeString);
  let parsedDateTimeWithZone = new Intl.DateTimeFormat("en-Us", {
    dateStyle: "short",
    timeStyle: "short",
    hour12: true,
    timeZone: userTimeZone
  }).format(dateTimeObj.toDate());

  return isformat
    ? parsedDateTimeWithZone
    : formatDateTime(parsedDateTimeWithZone, currentUser?.DateFormat);
}

export function isDateGreaterThanYear(date) {
  const currentUser = useUserStore.getState().currentUser;
  let userTimeZone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  let currentDateTimeOfUserZone = getTimeZoneOffset(userTimeZone);
  const inputDaysJsObj = dayjs(date).tz(userTimeZone);
  return Math.abs(inputDaysJsObj.diff(currentDateTimeOfUserZone, "days")) > 365;
}

//from date utils

let dateFormat, timeFormat, dateTimeFormatPattern;

let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let locale = "en-US";

const DATE_FORMAT = {
  DATETIME: "DateTime",
  TIMEONLY: "TimeOnly",
  DATEONLY: "DateOnly"
};

export const formatToLocaleForm = (
  dateString,
  withTime,
  convert,
  dateTimeFormat = DATE_FORMAT.DATEONLY,
  isComputedValue
) => {
  if (!dateString) return;
  if (!withTime && !dateTimeFormat) {
    dateTimeFormat = DATE_FORMAT.DATEONLY;
  }
  let parsedDateString;
  if (isComputedValue) {
    parsedDateString = dateString;
  } else {
    parsedDateString = parseDate(dateString, withTime, convert);
  }
  let value = parsedDateString && parsedDateString.split(/[^0-9]/);
  if (locale) {
    if (dateTimeFormat === DATE_FORMAT.DATEONLY) {
      dateTimeFormat = dateFormat || "YYYY-MM-DD";
    }
    if (dateTimeFormat === DATE_FORMAT.TIMEONLY) {
      dateTimeFormat = timeFormat || "HH:mm";
    }
    if (withTime || dateTimeFormat === DATE_FORMAT.DATETIME) {
      dateTimeFormat =
        dateTimeFormatPattern &&
        dateTimeFormatPattern
          .replace("{1}", dateFormat)
          .replace("{0}", timeFormat);
      if (!dateTimeFormat) {
        dateTimeFormat = "YYYY-MM-DD HH:mm";
      }
    }
    if (withTime) {
      value =
        value &&
        dayjs(
          new Date(value[0], value[1] - 1, value[2], value[3], value[4])
        ).format(dateTimeFormat);
    } else {
      value =
        value &&
        dayjs(new Date(value[0], value[1] - 1, value[2])).format(
          dateTimeFormat
        );
    }
  }
  return value;
};

export const parseFromLocale = (dateString, withTime) => {
  if (!dateString) {
    return;
  }
  let validDateFormats = [];
  let dateTimeFormat = dateFormat;
  let defaultDateTimeFormat = ["YYYY-MM-DD"];
  if (withTime) {
    dateTimeFormat =
      dateTimeFormatPattern &&
      dateTimeFormatPattern
        .replace("{1}", dateFormat)
        .replace("{0}", timeFormat);
    defaultDateTimeFormat = [
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD HH:mm A",
      "YYYY-MM-DDTHH:mm:ssZ"
    ];
  }
  if (dateTimeFormat && dateString.indexOf("-") >= 0) {
    validDateFormats = [
      dateTimeFormat,
      dateTimeFormat.replace("YY", "YYYY"),
      dateTimeFormat + " A",
      ...defaultDateTimeFormat
    ];
  } else if (dateTimeFormat) {
    validDateFormats = [dateTimeFormat.replace("YY", "YYYY")];
  } else {
    validDateFormats = [...defaultDateTimeFormat];
  }
  if (dateTimeFormatPattern === "{0}, {1}" && withTime) {
    let dateTime = validDateFormats[0].split(",");
    let format = dateTime[1].trim() + " " + dateTime[0];
    validDateFormats = [validDateFormats[0], format, format + " A"];
  }
  let momentDate = dayjs(dateString, validDateFormats);
  if (!momentDate.isValid()) {
    return null;
  }
  return momentDate.toDate();
};

export const dateFromString = (dateStrig) => {
  let dateTime = dateStrig.split(" ");
  let date;
  if (dateTime.length > 1) {
    date = new Date(dateTime[0]);
    let time = dateTime[1] && dateTime[1].split(":");
    date.setHours(time[0]);
    date.setMinutes(time[1]);
  } else {
    date = new Date(dateStrig);
  }
  return date;
};

//widgets

dayjs.parseTwoDigitYear = (yearString) => {
  const year = parseInt(yearString, 10);
  if (year > 50) return 1900 + year;
  else return 2000 + year;
};

const TIME_REGEX = /^([0-2][0-9]):[0-5][0-9] (am|pm)$/;

export function validTimeString(timeString) {
  return timeString && TIME_REGEX.test(timeString.toLowerCase());
}

export function timeStringToMinutes(timeString) {
  const [time, meridian] = timeString.split(" ");
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours === "12" ? "0" : hours, 10) * 60;
  minutes = parseInt(minutes, 10);
  return hours + minutes + (meridian.toLowerCase() === "pm" ? 720 : 0);
}

export function minutesToTimeString(val) {
  const minutes12 = val > 720 ? val - 720 : val;
  const meridian = val >= 720 ? "pm" : "am";
  let hours = Math.floor(minutes12 / 60);
  let minutes = minutes12 % 60;
  hours = (hours === 0 ? 12 : hours).toString().padStart(2, "0");
  minutes = minutes.toString().padStart(2, "0");
  return `${hours}:${minutes} ${meridian.toUpperCase()}`;
}

export function parseDateTime(datetimeString, format) {
  const datetime = dayjs(datetimeString, format, true);
  if (!datetime.isValid()) {
    throw new Error("Invalid Date");
  }
  return datetime;
}

export function formatDateStringToUTC(date, withTime) {
  if (!date) {
    return;
  }
  let month = String(date.getMonth() + 1).padStart(2, "0");
  let day = String(date.getDate()).padStart(2, "0");
  if (withTime) {
    const { currentUser } = useUserStore.getState();
    var timezone =
      (currentUser && currentUser.TimeZone._id) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    var offset = getOffset(date, timezone);
    month = date.getMonth();
    day = date.getDate();
    let year = date.getFullYear();
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let datetimeString = `${dayjs([year, month, day, hours, minutes])
      .utcOffset(offset, true)
      .format("YYYY-MM-DDTHH:mm:00Z")} ${timezone}`;
    return datetimeString;
  } else {
    return `${date.getFullYear()}-${month}-${day}`;
  }
}

export const formatDateToUTC = (date, withTime) => {
  if (!(date instanceof Date) && !date.$d) {
    date = date.toDate();
  }
  if (date.$d) {
    date = date.$d;
  }
  if (!date) {
    return;
  }
  let month = String(date.getMonth() + 1).padStart(2, "0");
  let day = String(date.getDate()).padStart(2, "0");
  if (withTime) {
    const { currentUser } = useUserStore.getState();
    var timezone =
      (currentUser && currentUser.TimeZone._id) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    var offset = getOffset(date, timezone);
    month = date.getMonth();
    day = date.getDate();
    let year = date.getFullYear();
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let datetimeString = `${dayjs([year, month, day, hours, minutes])
      .utcOffset(offset, true)
      .format("YYYY-MM-DDTHH:mm:00Z")} ${timezone}`;
    return datetimeString;
  } else {
    return `${date.getFullYear()}-${month}-${day}`;
  }
};

const getOffset = (date = new Date(), timezone) => {
  return dayjs.tz(date, timezone).utcOffset();
};

export const parseDate = (dateString, withTime, convertToTimezone) => {
  if (!dateString) return;
  try {
    if (withTime) {
      let timezone =
        dateString.split(" ")[1] ||
        userTimezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (convertToTimezone) {
        timezone = userTimezone;
      }
      let dateTimeString = dateString.split(" ")[0];
      return dayjs(dateTimeString).tz(timezone).format("YYYY-MM-DD HH:mm");
    }
  } catch (e) {
    console.warn("Could not parse Date", e);
  }
  return dateString;
};

export const formatToLocale = (dateString, withTime, forceSplit) => {
  const { currentUser } = useUserStore.getState();
  let locale =
    (currentUser && currentUser.DateFormat) ||
    Intl.DateTimeFormat().resolvedOptions().locale;
  let value;
  if (withTime) {
    value = formatDateTime(dateString, locale, forceSplit);
  } else {
    value = formatDate(dateString, locale);
  }
  return value;
};

const DATE_TIME_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\+(\d{2}):(\d{2})(?: ([\w\/]+))?$/;
// YYYY-MM-DDTHH:mm:ss+HH:mm (optional timezone)

export function isValidDatetime(dateString) {
  return DATE_TIME_REGEX.test(dateString);
}

function isISODateWithZ(dateString) {
  const isoWithZRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

  return isoWithZRegex.test(dateString) && dayjs(dateString).isValid();
}
